/* Copyright 2016 VMware, Inc. All rights reserved. -- VMware Confidential */

/// <reference path="../../../interfaces.ts"/>

namespace h5_vm {
   import ManagedObjectReference = com.vmware.vim.binding.vmodl.ManagedObjectReference;

   export class IpAllocationPolicy {
      static readonly DHCP: string = "DHCP";
      static readonly STATIC_MANUAL: string = "STATIC_MANUAL";
      static readonly STATIC_IPPOOL: string = "STATIC_IPPOOL";
      static readonly TRANSIENT_IPPOOL: string = "TRANSIENT_IPPOOL";
   }

   export class IpProtocol {
      static readonly IPV4: string = "IPV4";
      static readonly IPV6: string = "IPV6";
   }

   interface NetworkMappingPrefetchedNetwork extends PrefetchedNetwork {
      // Additional CisId record, that holds and 'id' value which CLS understands.
      cisId: string;
   }

   interface NetworkMappingListItem {
      idx: number;
      name: string;
      preselectedNetwork: NetworkMappingPrefetchedNetwork;
   }

   export class VmProvisioningNetworkMappingController {
      static $inject: string[] = [
         "$scope",
         "i18nService",
         "vuiConstants",
         "NetworkSelectorMode",
         "networkSelectorDialogService",
         "cisIdConverter",
         "defaultUriSchemeUtil",
         "managedTypesUtil",
      ];

      private i18n: any;
      private dataGridOptions: any;

      private ipAllocationParams: any;
      private networkMappings: NetworkMappingParams;

      public networkProviderId: string; // used by the network selector
      public mappingItems?: NetworkMappingListItem[]; // used by the network selector

      private networkSelectorFilterSpec: NetworkSelectorFilterSpec;
      private networkSelectorDialogSpec: NetworkSelectorDialogSpec;
      private sortParams: {field: string, dir: "asc"|"desc"}[];
      private prefetchedNetworks: NetworkMappingPrefetchedNetwork[];
      private defaultPreselectedNetwork: NetworkMappingPrefetchedNetwork;

      constructor(
            private $scope: any,
            private i18nService: any,
            private vuiConstants: any,
            private NetworkSelectorMode: any,
            private networkSelectorDialogService: any,
            private cisIdConverter: any,
            private defaultUriSchemeUtil: any,
            private managedTypesUtil: any,
      ) {
         this.i18n = this.i18nService.getString;
      }

      $onInit() {
         if (this.networkMappings) {
            this.initNetworkSelector();

            // Map the sourceNetworks objects to NetworkMappingListItem objects.
            this.mappingItems = this.buildListItems(this.networkMappings.sourceNetworks);

            this.dataGridOptions = {
               columnDefs: [
                  {
                     displayName: this
                           .i18nService
                           .getString("ProvisioningUiLib", "NetworkMappingsControl.templateNetworkColumnHeader"),
                     field: "name"
                  },
                  {
                     displayName: this
                           .i18nService
                           .getString("ProvisioningUiLib", "NetworkMappingsControl.selectedNetworkColumnHeader"),
                     template: (data: NetworkMappingListItem) => {
                        return this.buildDestinationNetworkDropdown(data);
                     }
                  }
               ],
               selectionMode: this.vuiConstants.grid.selectionMode.SINGLE,
               selectedItems: [],
               data: this.mappingItems,
            };

         }
         if (!_.isEmpty(this.ipAllocationParams)) {
            this.populateLocalizedNamesForIpAllocation();
            this.populateLocalizedNamesForIpProtocol();
         }
      }

      public shouldDisplayIpAllocation(): boolean {
         return !_.isEmpty(this.ipAllocationParams);
      }

      private initNetworkSelector() {
         this.networkSelectorFilterSpec = this.networkSelectorDialogService
               .createNetworkSelectorFilterSpec(
                     true/*includeStandardNetworks*/,
                     true/*includeOpaqueNetworks*/,
                     true/*includeDistributedPortGroups*/,
                     false/*includeUplinkPortGroups*/,
                     true/*includeOpaqueNetworkTypes*/,
                     true/*filterPerAssignableNetworks*/);
         this.sortParams = [{field:"name", dir:"asc"}];

         this.networkSelectorDialogSpec = this.networkSelectorDialogService
               .createNetworkSelectorDialogSpec(
                     ["Network.Assign"],
                     this.i18nService.getString(
                           "NetworkUi",
                           "AddNetworkingWizard.conTargetPage.noDvpgPrivilegeMessage")
               );

         this.prefetchedNetworks = this.buildPrefetchedNetworksFromTargetNetworks(
               this.networkMappings.targetNetworks);
         this.defaultPreselectedNetwork = this.prefetchedNetworks && this.prefetchedNetworks[0];
      }

      private buildDestinationNetworkDropdown(data: NetworkMappingListItem): string {
         const result = "<div network-selector " +
               "object-ref='$parent.$parent.$ctrl.networkProviderId' "+
               "mode='$parent.$parent.$ctrl.NetworkSelectorMode.SINGLE_SELECTION_DROPDOWN' "+
               "filter-spec='$parent.$parent.$ctrl.networkSelectorFilterSpec' "+
               "dialog-spec='$parent.$parent.$ctrl.networkSelectorDialogSpec' "+
               `preselected-network='$parent.$parent.$ctrl.mappingItems[${data.idx}].preselectedNetwork'`  +
               "prefetched-networks='$parent.$parent.$ctrl.prefetchedNetworks' " +
               `on-selected-networks='$parent.$parent.$ctrl.onSelectNetworks(selectedNetworks, ${data.idx})' ` +
               "sort-params='$parent.$parent.$ctrl.sortParams'>"+
               "</div>";
         return result;
      }

      private buildPrefetchedNetworksFromTargetNetworks(
            targetNetworks: TargetNetwork[]): NetworkMappingPrefetchedNetwork[] {
         const result = _.reduce(targetNetworks,
               (memo: NetworkMappingPrefetchedNetwork[], targetNetwork: TargetNetwork) => {
                  if (memo.length >= 10) {
                     return memo;
                  }

                  const parts: string[] = targetNetwork.id.split(":");
                  const value = parts[0];
                  const serverGuid = parts[1];
                  const type = this.managedTypesUtil.deductTypeFromGlobalCisId(targetNetwork.id);

                  if (targetNetwork.accessible) {
                     // generate the NetworkMappingPrefetchedNetwork record
                     memo.push({
                        name: targetNetwork.name,
                        id: this.defaultUriSchemeUtil.getVsphereObjectId( {
                           type: type,
                           value: value,
                           serverGuid: serverGuid
                        }),
                        cisId: targetNetwork.id
                     });
                  }

                  return memo;
               }, []);
         return result;
      }

      private getLocalizedIpAllocationPolicy(policy: string) {
         switch (policy) {
            case IpAllocationPolicy.STATIC_MANUAL:
               return this.i18nService.getString("ProvisioningUiLib", "IpAllocationControl.staticManual");
            case IpAllocationPolicy.DHCP:
               return this.i18nService.getString("ProvisioningUiLib", "IpAllocationControl.dhcp");
            case IpAllocationPolicy.STATIC_IPPOOL:
               return this.i18nService.getString("ProvisioningUiLib", "IpAllocationControl.staticIpPool");
            case IpAllocationPolicy.TRANSIENT_IPPOOL:
               return this.i18nService.getString("ProvisioningUiLib", "IpAllocationControl.transientIpPool");
         }
      }

      private getLocalizedIpProtocol(protocol: string) {
         switch (protocol) {
            case IpProtocol.IPV4:
               return this.i18nService.getString("ProvisioningUiLib", "IpAllocationControl.ipv4");
            case IpProtocol.IPV6:
               return this.i18nService.getString("ProvisioningUiLib", "IpAllocationControl.ipv6");
         }
      }

      private populateLocalizedNamesForIpAllocation() {
         this.ipAllocationParams.supportedIpAllocationPolicy = this.ipAllocationParams.supportedIpAllocationPolicy.map((o: any) => {
            o.localizedName = this.getLocalizedIpAllocationPolicy(o.name);
            return o;
         });
      }

      private populateLocalizedNamesForIpProtocol() {
         this.ipAllocationParams.supportedIpProtocol = this.ipAllocationParams.supportedIpProtocol.map((o: any)=> {
            o.localizedName = this.getLocalizedIpProtocol(o.name);
            return o;
         });
      }

      private buildListItems(sourceNetworks?: SourceNetwork[]): NetworkMappingListItem[]|undefined {
         const retVal = sourceNetworks && sourceNetworks.map(
            (item: SourceNetwork, idx: number) => {
               return {
                  idx: idx,
                  name: item.name,
                  preselectedNetwork: this.findPrefetchedNetworkByCisId(item.target) ||
                        this.defaultPreselectedNetwork
               };
            });
         return retVal;
      }

      public onSelectNetworks(
            selectedNetworks: {
               name: string, id: string, networkRef: ManagedObjectReference }[],
            rowIndex: number) {
         if (!this.networkMappings.sourceNetworks) {
            return;
         }

         const networkRef = selectedNetworks[0].networkRef;

         this.networkMappings.sourceNetworks[rowIndex].target = this.cisIdConverter.toGlobalCisId(networkRef);
      };

      private findPrefetchedNetworkByCisId(targetNetworkCisId?: string): NetworkMappingPrefetchedNetwork|undefined {
         const network = _.find(this.prefetchedNetworks || [], (nw: NetworkMappingPrefetchedNetwork) => {
            return nw.cisId === targetNetworkCisId;
         });
         return network;
      }
   }

   export class VmProvisioningNetworkMappingComponent {
      public controller: any;
      public templateUrl: string;
      public bindings: any;

      constructor() {
         this.controller = VmProvisioningNetworkMappingController;
         this.templateUrl = "vm-ui/resources/vm/views/createVmWizard/vmProvisioningNetworkMapping/vm-provisioning-network-mapping.html";
         this.bindings = {
            networkProviderId: "=",
            networkMappings: "=",
            ipAllocationParams: "=",
            isValidated: "="
         };
      }
   }

   angular.module("com.vmware.vsphere.client.vm")
         .component("vmProvisioningNetworkMapping", new VmProvisioningNetworkMappingComponent());

}
